// "Not you? End it": ending a session a sign-in from a PC started, from the link the player is sent afterwards. Public:
// the secret in the link's fragment is the authority, for 24 hours, and nobody has to sign in to use it (the person
// using it may be the one whose account someone else just used).
//
// The page takes the secret out of the URL at once and keeps it in memory only (a reload asks for the link again),
// asks "End the session on this PC?" and, on one press, ends it. Every answer is said in words; no raw code reaches
// the screen.

import type { SignInHandoffEnded } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button } from '@clavian/ui-web';
import { useMutation } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { useProblem } from '../api/use-problem';
import { AuthLayout } from '../auth/AuthLayout';
import { classifyHandoffFailure } from './StationSignInPage';
import { tokenFromFragment } from './handoff-token';

type EndFailure = 'expired' | 'rateLimited' | 'network' | 'generic';

const FAILURE_BODY: Record<Exclude<EndFailure, 'expired'>, string> = {
  rateLimited: 'stationSignIn.failure.rateLimited',
  network: 'stationSignIn.failure.network',
  generic: 'stationSignIn.failure.generic',
};

function classifyEndFailure(error: unknown): EndFailure {
  switch (classifyHandoffFailure(error)) {
    case 'unavailable':
    case 'invalid':
      return 'expired';
    case 'rateLimited':
      return 'rateLimited';
    case 'network':
      return 'network';
    default:
      return 'generic';
  }
}

export function EndSessionPage(): ReactNode {
  const { t } = useTranslation('player');
  const api = useApi();
  const problem = useProblem();
  const navigate = useNavigate();
  const location = useLocation();

  // Read once, on arrival; memory only.
  const [token] = useState(() => {
    const fragment = tokenFromFragment(location.hash);
    return typeof fragment === 'string' ? fragment : null;
  });
  // Take it out of the visible URL (and out of history) at once.
  const hadFragment = location.hash !== '';
  useEffect(() => {
    if (hadFragment) void navigate({ to: '/station-sign-in/end', replace: true, resetScroll: false });
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const end = useMutation<SignInHandoffEnded, unknown>({
    mutationFn: () => api.endSignInHandoffSession({ body: { token: token ?? '' } }),
  });
  const failure = end.isError ? classifyEndFailure(end.error) : null;

  // Focus follows what the page is saying (not on first render).
  const stateKey = token === null ? 'missing' : end.isSuccess ? `done:${String(end.data.ended)}` : failure === 'expired' ? 'expired' : 'ask';
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    document.querySelector<HTMLElement>('#pw-main h1')?.focus();
  }, [stateKey]);

  if (token === null) {
    return (
      <EndLayout title={t('stationSignIn.end.missingTitle')}>
        <p className="pw-handoff__body" role="alert">
          {t('stationSignIn.end.missing')}
        </p>
      </EndLayout>
    );
  }

  if (end.isSuccess) {
    const names = { station: end.data.stationName, venue: end.data.venueName };
    return end.data.ended ? (
      <EndLayout title={t('stationSignIn.end.doneTitle')}>
        <p className="pw-handoff__body" role="status">
          {t('stationSignIn.end.done', names)}
        </p>
      </EndLayout>
    ) : (
      <EndLayout title={t('stationSignIn.end.nothingTitle')}>
        <p className="pw-handoff__body" role="status">
          {t('stationSignIn.end.nothing', names)}
        </p>
      </EndLayout>
    );
  }

  if (failure === 'expired') {
    return (
      <EndLayout title={t('stationSignIn.end.expiredTitle')}>
        <p className="pw-handoff__body" role="alert">
          {t('stationSignIn.end.expired')}
        </p>
      </EndLayout>
    );
  }

  return (
    <EndLayout title={t('stationSignIn.end.title')}>
      <div className="pw-form">
        <p className="pw-handoff__body">{t('stationSignIn.end.body')}</p>
        {failure !== null ? (
          <p className="pw-handoff__body" role="alert">
            {failure === 'generic' ? problem.text(end.error, { fallback: t(FAILURE_BODY[failure]) }) : t(FAILURE_BODY[failure])}
          </p>
        ) : null}
        <Button variant="primary" size="lg" block busy={end.isPending} onClick={() => end.mutate()}>
          {t('stationSignIn.end.action')}
        </Button>
      </div>
    </EndLayout>
  );
}

function EndLayout({ title, children }: { readonly title: string; readonly children: ReactNode }): ReactNode {
  return (
    <AuthLayout title={title} homeLink={false}>
      {children}
    </AuthLayout>
  );
}
